import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import MemberStageRing from "../../components/userRing/MemberStageRing";
import KanbanBoard from "./KanbanBoard";
import TaskList from "./TaskList";
import CustomFilterBar, { DATE_OPTIONS } from "../../components/userRing/CustomFilterBar";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MuiTooltip from '@mui/material/Tooltip';
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { Menu } from "@mui/material";
import { Bar } from "react-chartjs-2";
import { stageAPI, tasksAPI } from "../../api/apiHelper";
import { Chart, CategoryScale, LinearScale, BarElement, Tooltip, Legend, ArcElement } from "chart.js";
import { downloadChartCSV } from "../../utils/chartCsv";
Chart.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ArcElement);


// Professional calendar dropdown filter for charts
const ChartDateFilter = ({ value, onChange, anchorEl, setAnchorEl, menuId }) => {
  const quickFilters = [
    { label: "All Time", value: "" },
    { label: "Last 7 Days", value: "7" },
    { label: "Last 30 Days", value: "30" },
    { label: "Last 90 Days", value: "90" }
  ];
  // Show custom input if value is already a custom range
  const [showCustom, setShowCustom] = useState(() => {
    return value && typeof value === 'object' && value.startDate && value.endDate;
  });
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [dateError, setDateError] = useState("");

  const handleClose = () => {
    setShowCustom(false);
    setAnchorEl(null);
  };

  const handleSelect = (filterValue) => {
    onChange(filterValue);
    handleClose();
  };

  const applyCustom = () => {
    if (!customStart || !customEnd) return;
    const start = new Date(customStart);
    const end = new Date(customEnd);
    if (end < start) {
      setDateError("End date cannot be before start date.");
      return;
    }
    setDateError("");
    const s = start.toISOString().split('T')[0];
    const e = end.toISOString().split('T')[0];
    onChange({ startDate: s, endDate: e });
    handleClose();
  };

  const cancelCustom = () => {
    setShowCustom(false);
  };

  const isCustomActive = value && typeof value === 'object' && value.startDate && value.endDate;

  return (
    <Menu
      id={ menuId }
      anchorEl={ anchorEl }
      open={ Boolean(anchorEl) }
      onClose={ handleClose }
      PaperProps={ {
        style: {
          minWidth: 220,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          borderRadius: 8,
          padding: 4
        }
      } }
    >
      { quickFilters.map((filter) => (
        <div
          key={ filter.value }
          onClick={ () => handleSelect(filter.value) }
          style={ {
            padding: "8px 16px",
            fontSize: "14px",
            fontWeight: (!isCustomActive && value === filter.value) ? 600 : 400,
            backgroundColor: (!isCustomActive && value === filter.value) ? "#f5f5f5" : "transparent",
            color: (!isCustomActive && value === filter.value) ? "#1976d2" : "#333",
            cursor: "pointer",
            transition: "all 0.2s",
            borderLeft: (!isCustomActive && value === filter.value) ? "3px solid #1976d2" : "3px solid transparent"
          } }
          onMouseEnter={ (e) => {
            if (value !== filter.value) {
              e.target.style.backgroundColor = "#f9f9f9";
            }
          } }
          onMouseLeave={ (e) => {
            if (value !== filter.value) {
              e.target.style.backgroundColor = "transparent";
            }
          } }
        >
          { filter.label }
        </div>
      )) }

      <div style={ { height: 1, backgroundColor: '#eee', margin: '6px 8px' } } />

      <div
        onClick={ () => setShowCustom(true) }
        style={ {
          padding: "8px 12px",
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          backgroundColor: isCustomActive ? '#f5f5f5' : 'transparent',
          borderLeft: isCustomActive ? '3px solid #1976d2' : '3px solid transparent'
        } }
      >
        <div style={ { fontSize: 14, fontWeight: isCustomActive ? 600 : 500 } }>Custom Range</div>
        <div style={ { fontSize: 12, color: '#666' } }>{ isCustomActive ? `${value.startDate} → ${value.endDate}` : '…' }</div>
      </div>

      { showCustom && (
        <div style={ { padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 8 } } onClick={ (e) => e.stopPropagation() }>
          <div style={ { display: 'flex', flexDirection: 'column', gap: 8 } }>
            <input type="date" value={ customStart } onChange={ (e) => {
              setCustomStart(e.target.value);
              setDateError("");
            } } style={ { flex: 1, padding: '6px 8px', marginBottom: 6 } } placeholder="Start date" />
            <input type="date" value={ customEnd } onChange={ (e) => {
              setCustomEnd(e.target.value);
              setDateError("");
            } } style={ { flex: 1, padding: '6px 8px', marginBottom: 6 } } placeholder="End date" />
            {/* Apply button just below end date */ }
            <div style={ { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 } }>
              <button onClick={ cancelCustom } style={ { padding: '6px 12px', cursor: 'pointer' } }>Cancel</button>
              <button
                onClick={ () => {
                  // Validation only on button click
                  if (!customStart || !customEnd) {
                    setDateError('Please select both dates.');
                    return;
                  }
                  const start = new Date(customStart);
                  const end = new Date(customEnd);
                  if (end < start) {
                    setDateError('End date cannot be before start date.');
                    return;
                  }
                  setDateError("");
                  const s = start.toISOString().split('T')[0];
                  const e = end.toISOString().split('T')[0];
                  onChange({ startDate: s, endDate: e });
                  handleClose();
                } }
                disabled={ !customStart || !customEnd }
                style={ { padding: '6px 12px', background: (!customStart || !customEnd) ? '#aaa' : '#1976d2', color: '#fff', border: 'none', cursor: (!customStart || !customEnd) ? 'not-allowed' : 'pointer' } }
              >Apply</button>
            </div>
            { dateError && <div style={ { color: 'red', fontSize: 12, marginTop: 4 } }>{ dateError }</div> }
          </div>
        </div>
      ) }
    </Menu>
  );
};

// Trigonometric Graph (SVG)
function TrigGraph({
  width = 680,
  height = 220,
  cycles = 2,
  amplitude = 0.9,
  phase = 0,
  color = "#1677ff",
  grid = true,
}) {
  const pad = 24;
  const w = width - pad * 2;
  const h = height - pad * 2;
  const midY = pad + h / 2;
  const steps = Math.max(2, Math.floor(w / 4));
  const toPath = (fn) => {
    let d = "";
    for (let i = 0; i <= steps; i++) {
      const x = pad + (i / steps) * w;
      const t = (i / steps) * (Math.PI * 2 * cycles) + phase;
      const y = midY - (h * 0.45) * amplitude * fn(t) - (h * 0.25);
      d += (i === 0 ? "M" : " L") + " " + x.toFixed(2) + " " + y.toFixed(2);
    }
    return d;
  };
  return (
    <svg width={ width } height={ height }>
      { grid && (
        <g opacity="0.25" stroke="#dcdcdc" strokeWidth="1">
          { [0, 0.25, 0.5, 0.75, 1].map((p, idx) => {
            const y = pad + p * h;
            return <line key={ "hg" + idx } x1={ pad } y1={ y } x2={ pad + w } y2={ y } />;
          }) }
          { [0, 0.25, 0.5, 0.75, 1].map((p, idx) => {
            const x = pad + p * w;
            return <line key={ "vg" + idx } x1={ x } y1={ pad } x2={ x } y2={ pad + h } />;
          }) }
        </g>
      ) }
      <line x1={ pad } y1={ midY } x2={ pad + w } y2={ midY } stroke="#999" strokeWidth="1.5" />
      <line x1={ pad } y1={ pad } x2={ pad } y2={ pad + h } stroke="#999" strokeWidth="1.5" />
      <path d={ toPath(Math.sin) } fill="none" stroke={ color } strokeWidth="2.5" />
      <text x={ pad + w - 6 } y={ midY - 6 } textAnchor="end" fontSize="11" fill="#666">x</text>
      <text x={ pad + 8 } y={ pad + 12 } fontSize="11" fill="#666">y</text>
    </svg>
  );
}

export default function UserDetails({ id: propId }) {
  const location = useLocation();
  const { state } = location;
  const { id: urlId } = useParams();
  const id = propId || urlId;

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);
  // Extract userId from query params (when manager clicks member ring)
  const urlParams = new URLSearchParams(location.search || "");
  const userIdFromQuery = urlParams.get("userId") || null;
  const navigate = useNavigate();
  const [selectedStageId, setSelectedStageId] = useState(null);
  const [dateRange, setDateRange] = useState("");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [status, setStatus] = useState("");

  const [selectedTask, setSelectedTask] = useState(null);
  // stageDealData removed — endpoint /tasks/self-stage-deal-count not used anymore
  const [tasksList, setTasksList] = useState(state?.tasks || []);
  const [tasksCollapsed, setTasksCollapsed] = useState(true);
  const VISIBLE_TASKS = 4;
  const STATUS_COLOR_MAP = {
    completed: { color: '#43a047', bg: 'rgba(67,160,71,0.12)' },
    'in-progress': { color: '#1976d2', bg: 'rgba(25,118,210,0.12)' },
    pending: { color: '#ffb300', bg: 'rgba(255,179,0,0.12)' },
    overdue: { color: '#f44336', bg: 'rgba(244,67,54,0.12)' },
    default: { color: '#757575', bg: 'rgba(117,117,117,0.08)' },
  };
  // keep tasksList in sync if navigation state provides tasks
  useEffect(() => {
    if (state?.tasks && Array.isArray(state.tasks) && state.tasks.length > 0) {
      setTasksList(state.tasks);
    }
  }, [state]);

  // refs for individual task DOM nodes so we can scroll/highlight a task navigated-to
  const taskRefs = useRef({});

  // If navigation included a selectedTask (from manager TaskList), set and scroll to it
  useEffect(() => {
    if (!state) return;
    const sTask = state.selectedTask || null;
    const sTaskId = state.selectedTaskId || (sTask && (sTask._id || sTask.id));
    if (sTask) setSelectedTask(sTask);
    if (sTaskId) {
      // allow tasksList to render first
      setTimeout(() => {
        try {
          const el = taskRefs.current[sTaskId];
          if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch (e) { }
      }, 150);
    }
  }, [state, tasksList]);

  // New states for user graphs
  const [pipelineSummary, setPipelineSummary] = useState(null);
  const [performanceMetrics, setPerformanceMetrics] = useState(null);
  const [leadSourceMetrics, setLeadSourceMetrics] = useState(null);
  const [teamTaskStats, setTeamTaskStats] = useState(null);
  const [fullscreenModal, setFullscreenModal] = useState(false);
  const [selectedGraph, setSelectedGraph] = useState(null);
  const [selectedGraphType, setSelectedGraphType] = useState(null);
  const [openTasksModal, setOpenTasksModal] = useState(false);
  const [taskModalTasks, setTaskModalTasks] = useState(null);
  const [modalDateFilter, setModalDateFilter] = useState("All");
  const [modalCustomStart, setModalCustomStart] = useState("");
  const [modalCustomEnd, setModalCustomEnd] = useState("");

  // Individual chart date filters
  const [funnelDateRange, setFunnelDateRange] = useState("");
  // key to force refetch even when same filter value is reapplied
  const [funnelFilterKey, setFunnelFilterKey] = useState(0);
  const handleFunnelDateChange = (val) => {
    setFunnelDateRange(val);
    setFunnelFilterKey((k) => k + 1);
  };
  const [sourcesDateRange, setSourcesDateRange] = useState("");
  const [perfDateRange, setPerfDateRange] = useState("");
  const [taskStatsDateRange, setTaskStatsDateRange] = useState("");
  // Top Customer Questions date filter
  const [questionsDateRange, setQuestionsDateRange] = useState("");
  const [questionsData, setQuestionsData] = useState(null);
  // Kanban-specific date filter (visible only on this UserDetails page)
  const [kanbanDateRange, setKanbanDateRange] = useState("");
  const [kanbanCustomStart, setKanbanCustomStart] = useState("");
  const [kanbanCustomEnd, setKanbanCustomEnd] = useState("");

  // Calendar dropdown anchors
  const [funnelAnchor, setFunnelAnchor] = useState(null);
  const [sourcesAnchor, setSourcesAnchor] = useState(null);
  const [perfAnchor, setPerfAnchor] = useState(null);
  const [taskStatsAnchor, setTaskStatsAnchor] = useState(null);
  const [questionsAnchor, setQuestionsAnchor] = useState(null);
  // kanbanAnchor removed; using CustomFilterBar instead of dropdown

  // State for team stage deal count
  const [stageDealCount, setStageDealCount] = useState(null);

  // Helper: build API params ({ startDate, endDate }) from the global filter
  const buildDateParamsForAPI = (dr, cs, ce) => {
    if (!dr) return {};
    // if dr is object with startDate/endDate
    if (typeof dr === 'object' && dr.startDate && dr.endDate) {
      return { startDate: dr.startDate, endDate: dr.endDate };
    }
    // prefer explicit custom dates
    if (cs && ce) {
      return { startDate: cs, endDate: ce };
    }
    // numeric quick filters (days)
    const days = Number(dr);
    if (!isNaN(days) && days > 0) {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - days);
      return { startDate: startDate.toISOString().split('T')[0], endDate: endDate.toISOString().split('T')[0] };
    }
    return {};
  };

  // Helper: value shape for ChartDateFilter / individual chart states
  const buildChartDateValue = (dr, cs, ce) => {
    if (!dr) return "";
    if (typeof dr === 'object' && dr.startDate && dr.endDate) return { startDate: dr.startDate, endDate: dr.endDate };
    if (cs && ce) return { startDate: cs, endDate: ce };
    const days = Number(dr);
    if (!isNaN(days) && days > 0) return String(days);
    return "";
  };

  // (no-op) removed unused helper

  const cardRefs = useRef({});
  const [member, setMember] = useState(state?.data
    || {
    memberName
      : "Demo User", stages: []
  });
  // Fetch team stage deal count for this user
  useEffect(() => {
    if (!id) return;
    // Custom filter: block API call if either date is missing
    const isCustom = typeof dateRange === 'object' && dateRange !== null;
    const customDatesFilled = isCustom ? (dateRange.startDate && dateRange.endDate) : (customStart && customEnd);
    if (isCustom && !customDatesFilled) return;
    if (!isCustom && dateRange === 'custom' && (!customStart || !customEnd)) return;
    async function fetchStageDealCount() {
      try {
        // This API returns data for all team members, so filter for this user
        const apiParams = buildDateParamsForAPI(dateRange, customStart, customEnd);
        const params = { ...(apiParams || {}), userId: id };
        const res = await stageAPI.getTeamStageDealCount(params);
        console.log("my res", res.data);
        let dataArr = [];
        if (Array.isArray(res)) dataArr = res;
        else if (Array.isArray(res?.data)) dataArr = res.data;
        else if (Array.isArray(res?.data?.data)) dataArr = res.data.data;
        else dataArr = [];
        const userData = dataArr.find(u => String(u.memberId) === String(id) || String(u.userId) === String(id));
        setStageDealCount(userData || null);
        if (userData) {
          setMember(prev => ({ ...prev, ...userData }));
        }
      } catch (err) {
        setStageDealCount(null);
        // Optionally log error
      }
    }
    fetchStageDealCount();
  }, [id, dateRange, customStart, customEnd, state]);

  // Stats calculation


  const stats = member
    ? {
      totalDeals: member.stages
        ? Object.values(member.stages).reduce((sum, count) => sum + count, 0)
        : 0,
      totalRevenue: 0, // jab revenue mile tab add karna
    }
    : { totalDeals: 0, totalRevenue: 0 };

  console.log("Member stats:", stats);

  console.log("Member stats000000000:", stats);
  useEffect(() => {
    if (!selectedStageId) return;
    const el = cardRefs.current[selectedStageId];
    if (el) el.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [selectedStageId]);

  useEffect(() => {
    if (!id) return;
    // Custom filter: block API call if either date is missing
    const isCustom = typeof dateRange === 'object' && dateRange !== null;
    const customDatesFilled = isCustom ? (dateRange.startDate && dateRange.endDate) : (customStart && customEnd);
    if (isCustom && !customDatesFilled) return;
    if (!isCustom && dateRange === 'custom' && (!customStart || !customEnd)) return;
    // removed call to /tasks/self-stage-deal-count/:userId — not needed on frontend

    // Fetch user-specific graphs data (send global date params if present)
    const fetchUserGraphs = async () => {
      try {
        console.log("Fetching user graphs for userId:", id);
        const apiParams = buildDateParamsForAPI(dateRange, customStart, customEnd);
        // Add userId to params if manager is viewing specific user
        if (userIdFromQuery) {
          apiParams.userId = userIdFromQuery;
        }

        const [pipeline, performance, leadSource, taskStats] = await Promise.all([
          stageAPI.getManagerPipelineSummary(apiParams, id),
          stageAPI.getTeamPerformanceMetrics(apiParams, id),
          stageAPI.getLeadSourceMetrics(apiParams, id),
          stageAPI.getTeamTaskStats(apiParams, id),
        ]);

        console.log("Pipeline data:", pipeline);
        console.log("Performance data:", performance);
        console.log("Lead source data:", leadSource);
        console.log("Task stats data:", taskStats);

        setPipelineSummary(pipeline.data || pipeline);
        setPerformanceMetrics(performance.data || performance);
        setLeadSourceMetrics(leadSource.data || leadSource);
        setTeamTaskStats(taskStats.data ? taskStats : (taskStats.data === undefined ? taskStats : null));
        // Fetch tasks for this user (manager can view a member's tasks via ?userId=)
        try {
          const tasksRes = await tasksAPI.getTasks(id, apiParams);
          // normalize response: apiRequest returns { data: [...] } inside rest
          let normalized = [];
          if (!tasksRes) normalized = [];
          else if (Array.isArray(tasksRes)) normalized = tasksRes;
          else if (Array.isArray(tasksRes.data)) normalized = tasksRes.data;
          else if (Array.isArray(tasksRes?.data?.data)) normalized = tasksRes.data.data;
          else normalized = [];
          setTasksList(normalized);
          console.log("Normalized tasks for user:", normalized.length, normalized.slice(0, 3));
        } catch (err) {
          console.error("Error fetching tasks for user:", err);
          // fall back to navigation state tasks if available
          setTasksList(state?.tasks || []);
        }
      } catch (err) {
        console.error("Error fetching user graphs:", err);
      }
    };
    fetchUserGraphs();
  }, [id, dateRange, customStart, customEnd, state, userIdFromQuery]);

  // When the global filter changes, propagate it to individual chart filters
  useEffect(() => {
    const val = buildChartDateValue(dateRange, customStart, customEnd);
    setFunnelDateRange(val);
    setSourcesDateRange(val);
    setPerfDateRange(val);
    setTaskStatsDateRange(val);
    setKanbanDateRange(val);
    setQuestionsDateRange(val);
  }, [dateRange, customStart, customEnd]);


  // Refetch data when individual chart filters change
  useEffect(() => {
    if (!id) return;
    const fetchPipelineWithFilter = async () => {
      try {
        const params = {};
        if (funnelDateRange && funnelDateRange !== "") {
          if (typeof funnelDateRange === 'object' && funnelDateRange.startDate && funnelDateRange.endDate) {
            params.startDate = funnelDateRange.startDate;
            params.endDate = funnelDateRange.endDate;
          } else {
            const days = Number(funnelDateRange);
            if (!isNaN(days) && days > 0) {
              const endDate = new Date();
              const startDate = new Date();
              startDate.setDate(endDate.getDate() - days);
              params.startDate = startDate.toISOString().split('T')[0];
              params.endDate = endDate.toISOString().split('T')[0];
            }
          }
        }
        // Add userId to params if manager is viewing specific user
        if (userIdFromQuery) {
          params.userId = userIdFromQuery;
        }
        const pipeline = await stageAPI.getManagerPipelineSummary(params, id);
        setPipelineSummary(pipeline.data || pipeline);
      } catch (err) {
        console.error("Error fetching pipeline with filter:", err);
      }
    };
    fetchPipelineWithFilter();
  }, [funnelDateRange, funnelFilterKey, id, userIdFromQuery]);

  useEffect(() => {
    if (!id) return;
    const fetchSourcesWithFilter = async () => {
      try {
        const params = {};
        if (sourcesDateRange && sourcesDateRange !== "") {
          if (typeof sourcesDateRange === 'object' && sourcesDateRange.startDate && sourcesDateRange.endDate) {
            params.startDate = sourcesDateRange.startDate;
            params.endDate = sourcesDateRange.endDate;
          } else {
            const days = Number(sourcesDateRange);
            if (!isNaN(days) && days > 0) {
              const endDate = new Date();
              const startDate = new Date();
              startDate.setDate(endDate.getDate() - days);
              params.startDate = startDate.toISOString().split('T')[0];
              params.endDate = endDate.toISOString().split('T')[0];
            }
          }
        }
        // Add userId to params if manager is viewing specific user
        if (userIdFromQuery) {
          params.userId = userIdFromQuery;
        }
        const leadSource = await stageAPI.getLeadSourceMetrics(params, id);

        // Client-side filtering based on date range
        const data = leadSource.data || leadSource;
        if (params.startDate && params.endDate && data) {
          const sT = new Date(params.startDate).setHours(0, 0, 0, 0);
          const eT = new Date(params.endDate).setHours(23, 59, 59, 999);

          const leads = Array.isArray(data.leads) ? data.leads : [];
          const deals = Array.isArray(data.deals) ? data.deals : [];

          const filteredItems = [];

          // Filter leads by created_time
          leads.forEach((l) => {
            const dt = l.created_time || l.createdAt;
            if (dt) {
              const t = new Date(dt).getTime();
              if (!isNaN(t) && t >= sT && t <= eT) {
                filteredItems.push({ source: l.source || l.lead_source || 'unknown' });
              }
            }
          });

          // Filter deals by createdAt
          deals.forEach((d) => {
            const dt = d.createdAt || d.created_time;
            if (dt) {
              const t = new Date(dt).getTime();
              if (!isNaN(t) && t >= sT && t <= eT) {
                filteredItems.push({ source: d.source || d.lead_source || 'unknown' });
              }
            }
          });

          // Rebuild metrics from filtered data
          const sourceCounts = {};
          filteredItems.forEach((item) => {
            const source = item.source || 'unknown';
            sourceCounts[source] = (sourceCounts[source] || 0) + 1;
          });

          const filteredMetrics = Object.keys(sourceCounts).map(source => ({
            source_name: source,
            lead_count: sourceCounts[source]
          }));

          setLeadSourceMetrics({ ...data, leadSourceMetrics: filteredMetrics });
        } else {
          setLeadSourceMetrics(data);
        }
      } catch (err) {
        console.error("Error fetching lead sources with filter:", err);
      }
    };
    fetchSourcesWithFilter();
  }, [sourcesDateRange, id, userIdFromQuery]);

  useEffect(() => {
    if (!id) return;
    const fetchPerfWithFilter = async () => {
      try {
        const params = {};
        if (perfDateRange && perfDateRange !== "") {
          // If user provided a custom range (object with startDate/endDate), prefer that
          if (typeof perfDateRange === 'object' && perfDateRange.startDate && perfDateRange.endDate) {
            params.startDate = perfDateRange.startDate;
            params.endDate = perfDateRange.endDate;
          } else {
            // numeric quick filters (days)
            const days = Number(perfDateRange);
            if (!isNaN(days) && days > 0) {
              const endDate = new Date();
              const startDate = new Date();
              startDate.setDate(endDate.getDate() - days);
              params.startDate = startDate.toISOString().split('T')[0];
              params.endDate = endDate.toISOString().split('T')[0];
            }
          }
        }
        // Add userId to params if manager is viewing specific user
        if (userIdFromQuery) {
          params.userId = userIdFromQuery;
        }

        // Always ask the API for filtered performance data. Remove client-side filtering
        const performance = await stageAPI.getTeamPerformanceMetrics(params, id);
        setPerformanceMetrics(performance.data || performance);
      } catch (err) {
        console.error("Error fetching performance with filter:", err);
      }
    };
    fetchPerfWithFilter();
  }, [perfDateRange, id, userIdFromQuery]);

  useEffect(() => {
    if (!id) return;
    const fetchTaskStatsWithFilter = async () => {
      try {
        const params = {};
        if (taskStatsDateRange && taskStatsDateRange !== "") {
          // support custom object ranges { startDate, endDate }
          if (typeof taskStatsDateRange === 'object' && taskStatsDateRange.startDate && taskStatsDateRange.endDate) {
            params.startDate = taskStatsDateRange.startDate;
            params.endDate = taskStatsDateRange.endDate;
          } else {
            const days = Number(taskStatsDateRange);
            if (!isNaN(days) && days > 0) {
              const endDate = new Date();
              const startDate = new Date();
              startDate.setDate(endDate.getDate() - days);
              params.startDate = startDate.toISOString().split('T')[0];
              params.endDate = endDate.toISOString().split('T')[0];
            }
          }
        }
        // Add userId to params if manager is viewing specific user
        if (userIdFromQuery) {
          params.userId = userIdFromQuery;
        }

        // Always request from API; avoid client-side date filtering
        const taskStats = await stageAPI.getTeamTaskStats(params, id);
        setTeamTaskStats(taskStats.data ? taskStats : (taskStats.data === undefined ? taskStats : null));
      } catch (err) {
        console.error("Error fetching task stats with filter:", err);
      }
    };
    fetchTaskStatsWithFilter();
  }, [taskStatsDateRange, id, userIdFromQuery]);

  // Fetch top questions for this user
  useEffect(() => {
    if (!id) return;
    const fetchQuestionsWithFilter = async () => {
      try {
        const params = { userId: id };
        if (questionsDateRange && questionsDateRange !== "") {
          if (typeof questionsDateRange === 'object' && questionsDateRange.startDate && questionsDateRange.endDate) {
            params.startDate = questionsDateRange.startDate;
            params.endDate = questionsDateRange.endDate;
          } else {
            const days = Number(questionsDateRange);
            if (!isNaN(days) && days > 0) {
              const endDate = new Date();
              const startDate = new Date();
              startDate.setDate(endDate.getDate() - days);
              params.startDate = startDate.toISOString().split('T')[0];
              params.endDate = endDate.toISOString().split('T')[0];
            }
          }
        }
        const res = await stageAPI.getTopQuestions(params);
        const categories = res?.data;
        if (!Array.isArray(categories)) return;
        const allQuestions = categories.flatMap(cat =>
          (Array.isArray(cat.questions) ? cat.questions : []).map(q => ({ ...q, category: q.category || cat.category }))
        ).filter(q => (q.importance_score || 0) > 5)
          .sort((a, b) => (b.frequency || 0) - (a.frequency || 0));
        setQuestionsData(allQuestions);
      } catch (err) {
        console.error("Error fetching top questions:", err);
      }
    };
    fetchQuestionsWithFilter();
  }, [questionsDateRange, id]);

  const email =
    state?.safeEmail ||
    member?.contact?.email ||
    member?.email ||
    `${(member?.memberName || "user").toLowerCase().replace(/\s+/g, ".")}@example.com`;

  // Helper function to render pipeline summary graph
  const renderPipelineSummary = () => {
    if (!pipelineSummary || !pipelineSummary.stageDetails) {
      return <div style={ { color: "#888", textAlign: "center", marginTop: 80, fontSize: 14 } }>Loading...</div>;
    }

    const stageDetails = pipelineSummary.stageDetails || [];
    const labels = stageDetails.map(s => s.stageName);
    const actualValues = stageDetails.map(s => s.total || 0);
    const colors = stageDetails.map((_, i) =>
      `rgba(${100 + ((i * 40) % 155)}, ${50 + ((i * 60) % 155)}, ${150 + ((i * 30) % 105)}, 0.85)`
    );

    return (
      <Bar
        data={ {
          labels,
          datasets: [{
            label: "Overall Lead Funnel",
            data: actualValues,
            actualValues: actualValues,
            backgroundColor: colors,
            borderWidth: 0,
            barThickness: 24
          }]
        } }
        options={ {
          indexAxis: "y",
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: function (context) {
                  return context.dataset.actualValues[context.dataIndex] + ' leads';
                }
              }
            }
          },
          scales: {
            x: {
              beginAtZero: true,
              title: {
                display: true,
                text: 'Number of Leads →',
                font: { size: 13, weight: 'normal' },
                color: '#666',
                padding: { top: 10, bottom: 5 }
              },
              ticks: {
                stepSize: 10,
                font: { size: 12, weight: 'normal' }
              },
              grid: { color: 'rgba(0,0,0,0.05)' }
            },
            y: {
              title: {
                display: true,
                text: 'Sales Stages →',
                font: { size: 13, weight: 'normal' },
                color: '#666',
                padding: { left: 20, right: 15 }
              },
              ticks: { font: { size: 12, weight: 'normal' } },
              grid: { display: false }
            }
          },
        } }
      />
    );
  };

  // Helpers for task-stage indicator and navigation
  const getStageColor = (stageName) => {
    if (!stageName) return { bgcolor: "#f8f9fa", color: "#495057", borderColor: "#e9ecef" };
    const stage = String(stageName).toLowerCase();
    if (stage.includes("closed won") || stage.includes("won")) return { bgcolor: "#e8f5e8", color: "#2e7d32", borderColor: "#c8e6c9" };
    if (stage.includes("negotiation") || stage.includes("stage 4") || stage.includes("stage4")) return { bgcolor: "#fff3cd", color: "#856404", borderColor: "#ffeaa7" };
    if (stage.includes("proposal") || stage.includes("stage 3") || stage.includes("stage3")) return { bgcolor: "#d4edda", color: "#155724", borderColor: "#c3e6cb" };
    if (stage.includes("qualification") || stage.includes("stage 2") || stage.includes("stage2")) return { bgcolor: "#cce7ff", color: "#004085", borderColor: "#bee5eb" };
    if (stage.includes("discovery") || stage.includes("stage 1") || stage.includes("stage1")) return { bgcolor: "#f8f9fa", color: "#495057", borderColor: "#e9ecef" };
    if (stage.includes("closed lost") || stage.includes("lost") || stage.includes("rejected")) return { bgcolor: "#ffebee", color: "#d32f2f", borderColor: "#ffcdd2" };
    return { bgcolor: "#f8f9fa", color: "#495057", borderColor: "#e9ecef" };
  };

  const getTaskStage = (task) => {
    if (!task) return null;
    return (
      task.stageName || task.stage || task.assignedStageName || task.stage_name || (task.deal && (task.deal.stageName || task.deal.stage)) || (task.raw && (task.raw.stageName || task.raw.stage)) || null
    );
  };

  // Fetch tasks for modal with date filter
  const fetchModalTasks = async (dateF, csStart, csEnd) => {
    try {
      const params = {};
      if (id) params.userId = id;
      if (dateF && dateF !== "All") {
        if (dateF === "custom" && csStart && csEnd) {
          params.startDate = csStart;
          params.endDate = csEnd;
        } else {
          const days = Number(dateF);
          if (!isNaN(days) && days > 0) {
            const end = new Date();
            const start = new Date();
            start.setDate(end.getDate() - days);
            params.startDate = start.toISOString().split("T")[0];
            params.endDate = end.toISOString().split("T")[0];
          }
        }
      }
      const res = await tasksAPI.getTasks(id, params);
      let normalized = [];
      if (Array.isArray(res)) normalized = res;
      else if (Array.isArray(res?.data)) normalized = res.data;
      else if (Array.isArray(res?.data?.data)) normalized = res.data.data;
      setTaskModalTasks(normalized);
    } catch (e) {
      console.error("Modal task fetch error", e);
    }
  };

  // Re-fetch modal tasks when filter changes
  useEffect(() => {
    if (!openTasksModal) return;
    if (modalDateFilter === "custom" && (!modalCustomStart || !modalCustomEnd)) return;
    fetchModalTasks(modalDateFilter, modalCustomStart, modalCustomEnd);
  }, [modalDateFilter, modalCustomStart, modalCustomEnd, openTasksModal]);

  const handleStageIconClick = (stageName) => {    try {
      // Open the TaskList modal and pass filtered tasks (or all tasks if none)
      if (!stageName) {
        setTaskModalTasks(tasksList || []);
        setOpenTasksModal(true);
        return;
      }
      const normalized = (tasksList || []).filter((t) => {
        const s = (getTaskStage(t) || "").toString().toLowerCase();
        return s && s.includes(stageName.toString().toLowerCase());
      });
      setTaskModalTasks(normalized);
      setOpenTasksModal(true);
    } catch (e) { }
  };

  // Helper function to render performance metrics (single-user horizontal chart)
  const renderPerformanceMetrics = () => {
    if (!performanceMetrics || !performanceMetrics.byMember) {
      return <div style={ { color: "#888", textAlign: "center", marginTop: 80, fontSize: 14 } }>Loading...</div>;
    }

    const userData = performanceMetrics.byMember.find((u) => String(u.userId) === String(id));
    if (!userData) {
      return <div style={ { color: "#888", textAlign: "center", marginTop: 80, fontSize: 14 } }>No data</div>;
    }

    // Team-specific wins logic
    const teamType = userData.teamType || '';
    const breakdown = userData.breakdown || {};
    
    // Standardize statuses for consistent Y-axis and Legend
    const labels = ['Wins', 'Closed Lost', 'Open'];
    // API returns breakdown.closed_won for sales wins; telesales_wins/sales_wins may also be present
    const winsValue = (teamType === 'telesales'
      ? (userData.telesales_wins || breakdown.wins || 0)
      : (userData.sales_wins || breakdown.closed_won || breakdown.wins || 0)) || 0;
    const closedLostValue = breakdown.closed_lost || 0;
    const openValue = (userData.open_items || []).length || breakdown.open || 0;
    
    const datasets = [
      {
        label: "Wins",
        data: [winsValue, 0, 0],
        backgroundColor: "rgba(76, 175, 80, 0.85)",
        actualValues: [winsValue, 0, 0],
        barThickness: 18,
      },
      {
        label: "Closed Lost",
        data: [0, closedLostValue, 0],
        backgroundColor: "rgba(244, 67, 54, 0.85)",
        actualValues: [0, closedLostValue, 0],
        barThickness: 18,
      },
      {
        label: "Open",
        data: [0, 0, openValue],
        backgroundColor: "rgba(255, 193, 7, 0.85)",
        actualValues: [0, 0, openValue],
        barThickness: 18,
      }
    ];

    return (
      <Bar
        data={ {
          labels,
          datasets,
        } }
        options={ {
          indexAxis: "y",
          responsive: true,
          maintainAspectRatio: false,
          plugins: { 
            legend: { 
              display: true,
              position: 'top',
              labels: {
                boxWidth: 12,
                font: { size: 12, weight: 'normal' }
              }
            },
            tooltip: {
              callbacks: {
                label: function (context) {
                  return context.dataset.label + ': ' + (context.dataset.actualValues?.[context.dataIndex] || context.raw);
                }
              }
            }
          },
          scales: {
            x: {
              stacked: true,
              beginAtZero: true,
              title: {
                display: true,
                text: "Number of Deals →",
                font: { size: 13, weight: "normal" },
                color: "#666",
                padding: { top: 10, bottom: 5 },
              },
              ticks: {
                stepSize: 1,
                callback: function (value) {
                  return Math.round(value);
                },
                font: { size: 12, weight: "normal" },
                maxTicksLimit: 8,
              },
              grid: { color: "rgba(0,0,0,0.05)" },
            },
            y: {
              stacked: true,
              title: {
                display: true,
                text: "Deal Status →",
                font: { size: 13, weight: "normal" },
                color: "#666",
                padding: { left: 20, right: 15 },
              },
              ticks: { font: { size: 12, weight: "normal" }, autoSkip: false },
              grid: { display: false },
            },
          },
        } }
      />
    );
  };

  // Helper function to render lead source metrics
  const renderLeadSourceMetrics = () => {
    if (!leadSourceMetrics || !leadSourceMetrics.leadSourceMetrics) {
      return <div style={ { color: "#888", textAlign: "center", marginTop: 80, fontSize: 14 } }>Loading...</div>;
    }

    const labels = leadSourceMetrics.leadSourceMetrics.map(s => s.source_name);
    const data = leadSourceMetrics.leadSourceMetrics.map(s => s.lead_count || 0);

    return (
      <Bar
        data={ {
          labels,
          datasets: [{
            label: "Lead Count",
            data,
            backgroundColor: labels.map((_, i) => `rgba(${100 + ((i * 40) % 155)}, ${50 + ((i * 60) % 155)}, ${150 + ((i * 30) % 105)}, 0.85)`),
            borderWidth: 0,
            maxBarThickness: 25,
          }]
        } }
        options={ {
          indexAxis: "y",
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { 
              display: true,
              position: 'top',
              labels: {
                boxWidth: 12,
                font: { size: 12, weight: 'normal' }
              }
            },
            tooltip: {
              mode: "nearest",
              intersect: true,
              callbacks: {
                label: function (context) {
                  return context.label + ': ' + context.parsed.x + ' leads';
                }
              }
            }
          },
          scales: {
            x: {
              beginAtZero: true,
              title: {
                display: true,
                text: 'Number of Leads →',
                font: { size: 13, weight: 'normal' },
                color: '#666',
                padding: { top: 10, bottom: 5 }
              },
              ticks: {
                stepSize: 1,
                font: { size: 12, weight: 'normal' },
                maxTicksLimit: 8,
                autoSkip: true
              },
              grid: { color: 'rgba(0,0,0,0.05)' }
            },
            y: {
              title: {
                display: true,
                text: 'Lead Sources →',
                font: { size: 13, weight: 'normal' },
                color: '#666',
                padding: { left: 20, right: 15 }
              },
              ticks: { font: { size: 12, weight: 'normal' } },
              grid: { display: false }
            }
          },
          elements: {
            bar: { borderRadius: 0, borderSkipped: false }
          }
        } }
      />
    );
  };

  // Helper function to render top customer questions
  const renderTopQuestions = (isFullscreen = false) => {
    if (!questionsData) {
      return <div style={ { color: "#888", textAlign: "center", marginTop: 80, fontSize: 14 } }>Loading...</div>;
    }
    if (questionsData.length === 0) {
      return <div style={ { color: "#888", textAlign: "center", marginTop: 80, fontSize: 14 } }>No data</div>;
    }
    const labels = questionsData.map(q => q.question.length > 28 ? q.question.slice(0, 28) + '…' : q.question);
    const counts = questionsData.map(q => q.frequency || 1);
    const colors = questionsData.map((_, i) =>
      `rgba(${100 + ((i * 40) % 155)}, ${50 + ((i * 60) % 155)}, ${150 + ((i * 30) % 105)}, 0.85)`
    );
    const chartHeight = Math.max(200, questionsData.length * 36);
    const chartEl = (
      <Bar
        data={ {
          labels,
          datasets: [{
            label: "Asked by (customers)",
            data: counts,
            originalLabels: questionsData.map(q => q.question),
            backgroundColor: colors,
            borderWidth: 0,
            maxBarThickness: 22,
          }]
        } }
        options={ {
          indexAxis: "y",
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                title: (items) => {
                  const full = questionsData[items[0].dataIndex]?.question || '';
                  const words = full.split(' ');
                  const lines = [];
                  let line = '';
                  words.forEach(w => {
                    if ((line + ' ' + w).trim().length > 40) { lines.push(line.trim()); line = w; }
                    else { line = (line + ' ' + w).trim(); }
                  });
                  if (line) lines.push(line);
                  return lines;
                },
                label: (ctx) => ` ${ctx.parsed.x} customer asked this`,
              }
            }
          },
          scales: {
            x: {
              beginAtZero: true,
              title: { display: true, text: "Number of Customers →", font: { size: 12, weight: 'normal' }, color: "#666", padding: { top: 8, bottom: 4 } },
              ticks: { stepSize: 1, font: { size: 11, weight: 'normal' }, maxTicksLimit: 6 },
              grid: { color: "rgba(0,0,0,0.05)" },
            },
            y: {
              title: { display: false },
              ticks: { font: { size: 11, weight: 'normal' }, crossAlign: 'far', padding: 6 },
              grid: { display: false },
              afterFit: (axis) => { axis.width = 160; },
            },
          },
          elements: { bar: { borderRadius: 2, borderSkipped: false } },
        } }
      />
    );
    if (isFullscreen) return chartEl;
    return (
      <div style={ { flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", scrollbarWidth: "none", msOverflowStyle: "none" } }>
        <style>{ `.ud-questions-scroll::-webkit-scrollbar { display: none; }` }</style>
        <div className="ud-questions-scroll" style={ { height: chartHeight, minHeight: "100%" } }>
          { chartEl }
        </div>
      </div>
    );
  };

  // Helper function to render task stats
  const renderTaskStats = () => {
    console.log("Rendering task stats, teamTaskStats:", teamTaskStats);

    if (!teamTaskStats) {
      return <div style={ { color: "#888", textAlign: "center", marginTop: 80, fontSize: 14 } }>Loading...</div>;
    }

    // Support multiple response shapes: { data: [...] } or { byMember: [...] } or direct array
    const dataArray =
      (teamTaskStats && Array.isArray(teamTaskStats.data) && teamTaskStats.data) ||
      (teamTaskStats && Array.isArray(teamTaskStats.byMember) && teamTaskStats.byMember) ||
      (Array.isArray(teamTaskStats) ? teamTaskStats : null);

    if (!dataArray || dataArray.length === 0) {
      return <div style={ { color: "#888", textAlign: "center", marginTop: 80, fontSize: 14 } }>No data</div>;
    }

    const userData = dataArray.find(u => String(u.memberId) === String(id) || String(u.userId) === String(id));
    console.log("Found user data:", userData);

    if (!userData) {
      return <div style={ { color: "#888", textAlign: "center", marginTop: 80, fontSize: 14 } }>No data for this user</div>;
    }

    const counts = userData.statusCounts || {};
    const labels = ['Pending', 'Overdue', 'Completed'];
    
    const datasets = [
      {
        label: "Pending",
        data: [counts.Pending || 0, 0, 0],
        backgroundColor: "rgba(255, 193, 7, 0.85)",
        actualValues: [counts.Pending || 0, 0, 0],
        barThickness: 18,
      },
      {
        label: "Overdue",
        data: [0, counts.Overdue || counts.overdue || 0, 0],
        backgroundColor: "rgba(244, 67, 54, 0.85)",
        actualValues: [0, counts.Overdue || counts.overdue || 0, 0],
        barThickness: 18,
      },
      {
        label: "Completed",
        data: [0, 0, counts.Completed || counts.completed || 0],
        backgroundColor: "rgba(76, 175, 80, 0.85)",
        actualValues: [0, 0, counts.Completed || counts.completed || 0],
        barThickness: 18,
      }
    ];

    return (
      <Bar
        data={ {
          labels,
          datasets,
        } }
        options={ {
          indexAxis: "y",
          responsive: true,
          maintainAspectRatio: false,
          plugins: { 
            legend: { 
              display: true,
              position: 'top',
              labels: {
                boxWidth: 12,
                font: { size: 12, weight: 'normal' }
              }
            }
          },
          scales: {
            x: {
              stacked: true,
              beginAtZero: true,
              title: {
                display: true,
                text: 'Number of Tasks →',
                font: { size: 13, weight: 'normal' },
                color: '#666',
                padding: { top: 10, bottom: 5 }
              },
              ticks: {
                stepSize: 1,
                callback: function (value) { return Math.round(value); },
                font: { size: 12, weight: 'normal' },
                maxTicksLimit: 8,
              },
              grid: { color: 'rgba(0,0,0,0.05)' }
            },
            y: {
              stacked: true,
              title: {
                display: true,
                text: 'Task Status →',
                font: { size: 13, weight: 'normal' },
                color: '#666',
                padding: { left: 20, right: 15 }
              },
              ticks: { font: { size: 12, weight: 'normal' }, autoSkip: false },
              grid: { display: false }
            }
          },
        } }
      />
    );
  };

  const contact =
    (typeof state?.safeContact === "string" && state.safeContact) ||
    state?.safeContact?.phone ||
    state?.safeContact?.mobile ||
    member?.contact?.phone ||
    member?.contact?.mobile ||
    member?.phone ||
    member?.mobile ||
    member?.phoneNumber ||
    "N/A";

  const lastCall = member.lastCall || "Not available";
  const mostContactPerson =
    member?.mostContactPerson ||
    member?.contact?.name ||
    member?.primaryContact?.name ||
    member?.owner?.name ||
    member?.accountOwner ||
    "N/A";

  if (!member) {
    return (
      <div style={ { padding: 16 } }>
        <button onClick={ () => {
          if (state?.from === 'manager-dashboard') {
            navigate('/home', { state: { activeTab: 'manager' } });
          } else {
            navigate(-1);
          }
        } }>← Back</button>
        <div style={ { marginTop: 12, color: "#666" } }>
          Open from Manager page to load member data.
        </div>
      </div>
    );
  }



  return (
    <div style={ { padding: 16 } }>
      {/* Top bar */ }
      <div style={ { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 } }>
        { (() => {
          const _ud = JSON.parse(localStorage.getItem("userData") || "{}");
          const _role = (_ud.role || "").toLowerCase();
          const _isMember = _role === "member" || (!_ud.isOrganizationCreated && _role !== "manager" && _role !== "admin");
          if (_isMember) return null;
          return (
            <button onClick={ () => {
              if (state?.from === 'manager-dashboard') {
                navigate('/home', { state: { activeTab: 'manager' } });
              } else {
                navigate(-1);
              }
            } } style={ {
              fontWeight: 600,
              background: "#fff",
              border: "1.5px solid #e3e6f0",
              padding: "8px 16px",
              borderRadius: 8,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
              color: "#151d3b",
              transition: "all 0.2s",
              boxShadow: "0 2px 4px rgba(0,0,0,0.02)"
            } }>
              ← Back
            </button>
          );
        })() }
        <CustomFilterBar
          dateRange={ dateRange }
          setDateRange={ setDateRange }
          customStart={ customStart }
          setCustomStart={ setCustomStart }
          customEnd={ customEnd }
          setCustomEnd={ setCustomEnd }
          status={ status }
          setStatus={ setStatus }
          DATE_OPTIONS={ DATE_OPTIONS }
          dateLabel="Global Date-Range"

        />
      </div>

      {/* 3-part grid row */ }
      <div style={ { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 24, alignItems: "center", justifyItems: "center", marginBottom: 24 } }>
        <div>
          <MemberStageRing
            member={ member }
            size={ 250 }
            selectedStageId={ selectedStageId }
            onStageClick={ (seg) => setSelectedStageId(String(seg.stageId || seg._id)) }
          />
          {/* Stage-wise deal count below the ring */ }
          {/* {renderStageDealCount()} */ }
        </div>

        {/* Tasks */}
        {<div style={ { width: "100%", minHeight: 180, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start" } }>
          <div style={ { display: 'flex', alignItems: 'center', justifyContent: 'center', width: "100%", maxWidth: 380, marginBottom: 8, position: 'relative' } }>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a1a1a', textAlign: 'center' }}>Tasks</h3>
            <MuiTooltip title="Open full task list">
              <IconButton
                size="small"
                onClick={ (e) => {
                  e.stopPropagation();
                  try {
                    setModalDateFilter("All");
                    setModalCustomStart("");
                    setModalCustomEnd("");
                    setTaskModalTasks(tasksList || []);
                    setOpenTasksModal(true);
                  } catch (err) { }
                } }
                aria-label="open-tasks-full"
                sx={ { padding: '4px', position: 'absolute', right: 0 } }
              >
                <FullscreenIcon fontSize="small" />
              </IconButton>
            </MuiTooltip>
          </div>
          <div style={ { width: "100%", maxWidth: 380, background: "#fff", border: "1px solid #eee", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.04)", padding: "12px", minHeight: 120 } }>
            { (!tasksList || tasksList.length === 0) ? (
              <div style={ { textAlign: "center", color: "#888", padding: "24px 0" } }>No tasks found.</div>
            ) : (
              <>
                <div style={ { maxHeight: 220, overflowY: 'auto', paddingRight: 6 } }>
                  { (tasksCollapsed ? tasksList.slice(0, VISIBLE_TASKS) : tasksList).map((task) => {
                    const key = String(task.status || '').toLowerCase();
                    const statusCfg = STATUS_COLOR_MAP[key] || STATUS_COLOR_MAP.default;
                    const statusColor = statusCfg.color;
                    const statusBg = statusCfg.bg;
                    const isSelected = selectedTask && (String(selectedTask._id || selectedTask.id) === String(task._id || task.id));
                    return (
                      <div
                        key={ task._id || task.id }
                        ref={ (el) => { try { taskRefs.current[task._id || task.id] = el; } catch (e) { } } }
                        onClick={ () => {
                          setTaskModalTasks([task]);
                          setOpenTasksModal(true);
                        } }
                        style={ {
                          display: 'flex',
                          gap: 12,
                          alignItems: 'center',
                          padding: 12,
                          background: isSelected ? '#e8f0ff' : '#fff',
                          border: isSelected ? '2px solid #1976d2' : '1px solid #f1f1f1',
                          borderRadius: 10,
                          marginBottom: 10,
                          boxShadow: '0 1px 6px rgba(0,0,0,0.04)',
                          cursor: 'pointer'
                        } }>
                        <div style={ { width: 6, height: 48, borderRadius: 6, background: statusColor, flex: '0 0 6px' } } />
                        <div style={ { flex: 1, minWidth: 0 } }>
                          <div style={ { fontWeight: 700, color: '#222', fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }>{ task.taskTitle || task.title || task.name }</div>
                          <div style={ { display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 } }>
                            <div style={ { color: '#666', fontSize: 12 } }>{ task.assignedUser?.name || task.assignedUser?.email || '' }</div>
                            <div style={ { color: '#aaa', fontSize: 12 } }>{ task.createdAt ? String(task.createdAt).slice(0, 10) : '' }</div>
                          </div>
                        </div>
                        <div style={ { marginLeft: 8, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' } }>
                          <div style={ { color: statusColor, fontWeight: 700, fontSize: 12, border: '1px solid rgba(0,0,0,0.06)', padding: '4px 8px', borderRadius: 8, background: statusBg } }>{ String(task.status || '').charAt(0).toUpperCase() + String(task.status || '').slice(1) }</div>
                        </div>
                      </div>
                    );
                  }) }
                </div>

                { tasksList.length > VISIBLE_TASKS && (
                  <div style={ { display: 'flex', justifyContent: 'center', marginTop: 6 } }>
                    <button onClick={ () => setTasksCollapsed(!tasksCollapsed) } style={ { border: 'none', background: 'transparent', color: '#1976d2', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 } }>
                      { tasksCollapsed ? 'Show more' : 'Show less' } { tasksCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" /> }
                    </button>
                  </div>
                ) }
              </>
            ) }
          </div>
        </div> } 

        {/* Person Details */ }
        <div style={ { padding: 16, minHeight: 180, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-end", position: "relative" } }>
          <div style={ { display: "flex", flexDirection: "column", justifyContent: "flex-start", gap: 20, width: "min(320px, 100%)", fontSize: 16, alignSelf: "flex-start" } }>
            <div><strong>Name:</strong> { member?.memberName || "N/A" }</div>
            <div><strong>Email:</strong> { email || "demo@example.com" }</div>
            <div><strong>Phone No:</strong> { contact || "N/A" }</div>
            <div><strong>Number of Deals:</strong> { stats.totalDeals }</div>
            <div><strong>Last Call:</strong> { lastCall }</div>
            <div><strong>Most Contact Person:</strong> { mostContactPerson }</div>
          </div>
        </div>
      </div>

      {/* Center User Graphs - Manager Dashboard Style */ }
      <div className="user-details-scroll-container" style={ { overflowX: "auto", paddingBottom: "12px", width: "100%", scrollbarWidth: "auto", msOverflowStyle: "auto" } }>
        <style>{ `.user-details-scroll-container::-webkit-scrollbar { display: block; height: 12px; } .user-details-scroll-container::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 6px; } .user-details-scroll-container::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 6px; } .user-details-scroll-container::-webkit-scrollbar-thumb:hover { background: #a8a8a8; } .user-details-scroll-container::-webkit-scrollbar-corner { background: #f1f1f1; }` }</style>
        <div style={ { display: "grid", gridTemplateColumns: "repeat(5, 1fr)", minWidth: "1600px", gap: 16, marginBottom: 24 } }>

          {/* 1st Graph: Overall Lead Funnel */ }
          <div style={ { border: "1px solid #e0e0e0", borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', backgroundColor: '#fff' } }>
            <div
              style={ {
                background: "#fff",
                borderRadius: 12,
                padding: 20,
                height: 320,
                display: "flex",
                flexDirection: "column",
                boxSizing: "border-box",
              } }
            >
              <div
                style={ {
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 8,
                } }
              >
                <h3 style={ { margin: 0 } }>Overall Lead Funnel</h3>
                <div style={ { display: "flex", gap: 6, alignItems: "center" } }>
                  <IconButton
                    size="small"
                    sx={ { padding: "4px" } }
                    onClick={ (e) => setFunnelAnchor(e.currentTarget) }
                    aria-label="date-filter"
                    title="Filter by date range"
                  >
                    <CalendarTodayIcon fontSize="small" />
                  </IconButton>
                  <ChartDateFilter
                    value={ funnelDateRange }
                    onChange={ handleFunnelDateChange }
                    anchorEl={ funnelAnchor }
                    setAnchorEl={ setFunnelAnchor }
                    menuId="funnel-date-menu"
                  />
                  <IconButton
                    size="small"
                    sx={ { padding: "4px" } }
                    onClick={ () => {
                      if (pipelineSummary && pipelineSummary.stageDetails) {
                        const stageDetails = pipelineSummary.stageDetails || [];
                        const labels = stageDetails.map(s => s.stageName);
                        const actualValues = stageDetails.map(s => s.total || 0);
                        const colors = stageDetails.map((_, i) =>
                          `rgba(${100 + ((i * 40) % 155)}, ${50 + ((i * 60) % 155)}, ${150 + ((i * 30) % 105)}, 0.85)`
                        );
                        setSelectedGraph({ title: "Overall Lead Funnel" });
                        setSelectedGraphType('funnel');
                        setFullscreenModal(true);
                      }
                    } }
                    aria-label="expand-chart"
                    title="Expand chart"
                  >
                    <FullscreenIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={ () => {
                      try {
                        const stageDetails = (pipelineSummary && pipelineSummary.stageDetails) || [];
                        const labels = stageDetails.map((s) => s.stageName);
                        const actualValues = stageDetails.map((s) => s.total || 0);
                        const funnelData = {
                          labels,
                          datasets: [
                            {
                              label: "Overall Lead Funnel",
                              data: actualValues,
                            },
                          ],
                        };
                        const fname = `${(member?.name || 'user').toString().replace(/\s+/g, '_').toLowerCase()}_overall_lead_funnel.csv`;
                        downloadChartCSV(funnelData, { filename: fname, includeAllDatasets: true });
                      } catch (e) { }
                    } }
                    aria-label="download-funnel-csv"
                    title="Download CSV"
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </div>
              </div>
              <div style={ { flex: 1, minHeight: 0, marginTop: 8 } }>
                { renderPipelineSummary() }
              </div>
            </div>
          </div>

          {/* 2nd Graph: Leads by Source */ }
          <div style={ { border: "1px solid #e0e0e0", borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', backgroundColor: '#fff' } }>
            <div
              style={ {
                background: "#fff",
                borderRadius: 12,
                padding: 20,
                height: 320,
                display: "flex",
                flexDirection: "column",
                boxSizing: "border-box",
              } }
            >
              <div
                style={ {
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 8,
                } }
              >
                <h3 style={ { margin: 0 } }>Leads by Source</h3>
                <div style={ { display: "flex", gap: 6, alignItems: "center" } }>
                  <IconButton
                    size="small"
                    sx={ { padding: "4px" } }
                    onClick={ (e) => setSourcesAnchor(e.currentTarget) }
                    aria-label="date-filter"
                    title="Filter by date range"
                  >
                    <CalendarTodayIcon fontSize="small" />
                  </IconButton>
                  <ChartDateFilter
                    value={ sourcesDateRange }
                    onChange={ setSourcesDateRange }
                    anchorEl={ sourcesAnchor }
                    setAnchorEl={ setSourcesAnchor }
                    menuId="sources-date-menu"
                  />
                  <IconButton
                    size="small"
                    sx={ { padding: "4px" } }
                    onClick={ () => {
                      if (leadSourceMetrics && leadSourceMetrics.leadSourceMetrics) {
                        const labels = leadSourceMetrics.leadSourceMetrics.map(s => s.source_name);
                        const data = leadSourceMetrics.leadSourceMetrics.map(s => s.lead_count || 0);
                        const colors = labels.map((_, i) => `rgba(${100 + ((i * 40) % 155)}, ${50 + ((i * 60) % 155)}, ${150 + ((i * 30) % 105)}, 0.85)`);
                        setSelectedGraph({ title: "Leads by Source" });
                        setSelectedGraphType('sources');
                        setFullscreenModal(true);
                      }
                    } }
                    aria-label="expand-chart"
                    title="Expand chart"
                  >
                    <FullscreenIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={ () => {
                      try {
                        if (leadSourceMetrics && leadSourceMetrics.leadSourceMetrics) {
                          const labels = leadSourceMetrics.leadSourceMetrics.map(s => s.source_name);
                          const data = leadSourceMetrics.leadSourceMetrics.map(s => s.lead_count || 0);
                          const chartData = { labels, datasets: [{ label: 'Lead Count', data }] };
                          const fname = `${(member?.name || 'user').toString().replace(/\s+/g, '_').toLowerCase()}_leads_by_source.csv`;
                          downloadChartCSV(chartData, { filename: fname, includeAllDatasets: true });
                        }
                      } catch (e) { }
                    } }
                    aria-label="download-sources-csv"
                    title="Download CSV"
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </div>
              </div>
              <div style={ { height: "calc(100% - 36px)", width: "100%" } }>
                { renderLeadSourceMetrics() }
              </div>
            </div>
          </div>

          {/* 3rd Graph: Performance Metrics */ }
          <div style={ { border: "1px solid #e0e0e0", borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', backgroundColor: '#fff' } }>
            <div
              style={ {
                background: "#fff",
                borderRadius: 12,
                padding: 20,
                height: 320,
                display: "flex",
                flexDirection: "column",
                boxSizing: "border-box",
              } }
            >
              <div
                style={ {
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 8,
                } }
              >
                <h3 style={ { margin: 0 } }>Performance Breakdown</h3>
                <div style={ { display: "flex", gap: 6, alignItems: "center" } }>
                  <IconButton
                    size="small"
                    sx={ { padding: "4px" } }
                    onClick={ (e) => setPerfAnchor(e.currentTarget) }
                    aria-label="date-filter"
                    title="Filter by date range"
                  >
                    <CalendarTodayIcon fontSize="small" />
                  </IconButton>
                  <ChartDateFilter
                    value={ perfDateRange }
                    onChange={ setPerfDateRange }
                    anchorEl={ perfAnchor }
                    setAnchorEl={ setPerfAnchor }
                    menuId="perf-date-menu"
                  />
                  <IconButton
                    size="small"
                    sx={ { padding: "4px" } }
                    onClick={ () => {
                      if (performanceMetrics && performanceMetrics.byMember) {
                        const userData = performanceMetrics.byMember.find(u => u.userId === id);
                        if (userData) {
                          const breakdown = userData.breakdown || {};
                          const labels = Object.keys(breakdown);
                          const data = Object.values(breakdown);
                          const colors = labels.map(l => {
                            const lower = String(l).toLowerCase();
                            if (lower.includes('won')) return "rgba(76,175,80,0.85)";
                            if (lower.includes('lost')) return "rgba(244,67,54,0.85)";
                            if (lower.includes('open')) return "rgba(255,193,7,0.85)";
                            return `rgba(100,100,100,0.85)`;
                          });
                          setSelectedGraph({ title: "Performance Breakdown" });
                          setSelectedGraphType('performance');
                          setFullscreenModal(true);
                        }
                      }
                    } }
                    aria-label="expand-chart"
                    title="Expand chart"
                  >
                    <FullscreenIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={ () => {
                      try {
                        if (performanceMetrics && performanceMetrics.byMember) {
                          const userData = performanceMetrics.byMember.find(u => String(u.userId) === String(id));
                          if (userData) {
                            const breakdown = userData.breakdown || {};
                            const labels = Object.keys(breakdown);
                            const data = Object.values(breakdown).map(v => Math.round(Number(v) || 0));
                            const chartData = { labels, datasets: [{ label: 'Count', data }] };
                            const fname = `${(member?.name || 'user').toString().replace(/\s+/g, '_').toLowerCase()}_performance_breakdown.csv`;
                            downloadChartCSV(chartData, { filename: fname, includeAllDatasets: true });
                          }
                        }
                      } catch (e) { }
                    } }
                    aria-label="download-performance-csv"
                    title="Download CSV"
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </div>
              </div>
              <div style={ { height: "calc(100% - 36px)", width: "100%" } }>
                { renderPerformanceMetrics() }
              </div>
            </div>
          </div>

          {/* 4th Graph: Task Statistics */ }
          {<div style={ { border: "1px solid #e0e0e0", borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', backgroundColor: '#fff' } }>
            <div
              style={ {
                background: "#fff",
                borderRadius: 12,
                padding: 20,
                height: 320,
                display: "flex",
                flexDirection: "column",
                boxSizing: "border-box",
              } }
            >
              <div
                style={ {
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 8,
                } }
              >
                <h3 style={ { margin: 0 } }>Task Statistics</h3>
                <div style={ { display: "flex", gap: 6, alignItems: "center" } }>
                  <IconButton
                    size="small"
                    sx={ { padding: "4px" } }
                    onClick={ (e) => setTaskStatsAnchor(e.currentTarget) }
                    aria-label="date-filter"
                    title="Filter by date range"
                  >
                    <CalendarTodayIcon fontSize="small" />
                  </IconButton>
                  <ChartDateFilter
                    value={ taskStatsDateRange }
                    onChange={ setTaskStatsDateRange }
                    anchorEl={ taskStatsAnchor }
                    setAnchorEl={ setTaskStatsAnchor }
                    menuId="task-stats-date-menu"
                  />
                  <IconButton
                    size="small"
                    sx={ { padding: "4px" } }
                    onClick={ () => {
                      if (teamTaskStats && (teamTaskStats.data || teamTaskStats.byMember)) {
                        const list = (Array.isArray(teamTaskStats.data) && teamTaskStats.data) || (Array.isArray(teamTaskStats.byMember) && teamTaskStats.byMember) || [];
                        const userData = list.find(u => String(u.memberId) === String(id) || String(u.userId) === String(id));
                        if (userData) {
                          const statusCounts = userData.statusCounts || {};
                          const labels = Object.keys(statusCounts);
                          const data = Object.values(statusCounts);
                          const colors = labels.map(l => {
                            const lower = String(l).toLowerCase();
                            if (lower.includes('won') || lower.includes('completed')) return "rgba(76,175,80,0.85)";
                            if (lower.includes('lost') || lower.includes('overdue')) return "rgba(244,67,54,0.85)";
                            if (lower.includes('open') || lower.includes('pending')) return "rgba(255,193,7,0.85)";
                            if (lower.includes('progress')) return "rgba(25,118,210,0.85)";
                            return `rgba(150,150,150,0.85)`;
                          });
                          setSelectedGraph({ title: "Task Statistics" });
                          setSelectedGraphType('tasks');
                          setFullscreenModal(true);
                        }
                      }
                    } }
                    aria-label="expand-chart"
                    title="Expand chart"
                  >
                    <FullscreenIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={ () => {
                      try {
                        if (teamTaskStats && (teamTaskStats.data || teamTaskStats.byMember)) {
                          const list = (Array.isArray(teamTaskStats.data) && teamTaskStats.data) || (Array.isArray(teamTaskStats.byMember) && teamTaskStats.byMember) || [];
                          const userData = list.find(u => String(u.memberId) === String(id) || String(u.userId) === String(id));
                          if (userData) {
                            const statusCounts = userData.statusCounts || {};
                            const labels = Object.keys(statusCounts);
                            const data = Object.values(statusCounts).map(v => Number(v) || 0);
                            const chartData = { labels, datasets: [{ label: 'Task Count', data }] };
                            const fname = `${(member?.name || 'user').toString().replace(/\s+/g, '_').toLowerCase()}_task_statistics.csv`;
                            downloadChartCSV(chartData, { filename: fname, includeAllDatasets: true });
                          }
                        }
                      } catch (e) { }
                    } }
                    aria-label="download-tasks-csv"
                    title="Download CSV"
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </div>
              </div>
              <div style={ { height: "calc(100% - 36px)", width: "100%" } }>
                { renderTaskStats() }
              </div>
            </div>
          </div> }

          {/* 5th Graph: Top Customer Questions */ }
          <div style={ { border: "1px solid #e0e0e0", borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', backgroundColor: '#fff' } }>
            <div style={ { background: "#fff", borderRadius: 12, padding: 20, height: 320, display: "flex", flexDirection: "column", boxSizing: "border-box" } }>
              <div style={ { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 } }>
                <h3 style={ { margin: 0 } }>Top Customer Questions</h3>
                <div style={ { display: "flex", gap: 6, alignItems: "center" } }>
                  <IconButton size="small" sx={ { padding: "4px" } } onClick={ (e) => setQuestionsAnchor(e.currentTarget) } aria-label="date-filter" title="Filter by date range">
                    <CalendarTodayIcon fontSize="small" />
                  </IconButton>
                  <ChartDateFilter value={ questionsDateRange } onChange={ setQuestionsDateRange } anchorEl={ questionsAnchor } setAnchorEl={ setQuestionsAnchor } menuId="user-questions-date-menu" />
                  <IconButton size="small" sx={ { padding: "4px" } }
                    onClick={ () => { setSelectedGraph({ title: "Top Customer Questions" }); setSelectedGraphType('questions'); setFullscreenModal(true); } }
                    aria-label="expand-chart" title="Expand chart">
                    <FullscreenIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small"
                    onClick={ () => {
                      try {
                        if (questionsData && questionsData.length > 0) {
                          const chartData = { labels: questionsData.map(q => q.question), datasets: [{ label: 'Frequency', data: questionsData.map(q => q.frequency || 1) }] };
                          const fname = `${(member?.name || 'user').toString().replace(/\s+/g, '_').toLowerCase()}_top_customer_questions.csv`;
                          downloadChartCSV(chartData, { filename: fname, includeAllDatasets: true });
                        }
                      } catch (e) { }
                    } }
                    aria-label="download-questions-csv" title="Download CSV">
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </div>
              </div>
              <div style={ { flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" } }>
                { renderTopQuestions() }
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* KanbanBoard */ }
      <div style={ { marginTop: 12 } }>


        {/* Kanban filter moved into KanbanBoard to appear under Add Deal button */ }
        { console.log("KANBANBOARD PROPS", {
          member: member._id,
          selectedStageId,
          dateFilterParams: buildDateParamsForAPI(kanbanDateRange, kanbanCustomStart, kanbanCustomEnd),
          kanbanDateRange,
          kanbanCustomStart,
          kanbanCustomEnd
        }) }
        <KanbanBoard
          member={ member._id }
          selectedStageId={ selectedStageId }
          dateFilterParams={ buildDateParamsForAPI(kanbanDateRange, kanbanCustomStart, kanbanCustomEnd) }
          showAddStage={ false }
          kanbanDateRange={ kanbanDateRange }
          setKanbanDateRange={ setKanbanDateRange }
          kanbanCustomStart={ kanbanCustomStart }
          setKanbanCustomStart={ setKanbanCustomStart }
          kanbanCustomEnd={ kanbanCustomEnd }
          setKanbanCustomEnd={ setKanbanCustomEnd }
        />


      </div>


      {/* TaskList Modal — Kanban-style pipeline view */ }
      <Dialog
        open={ openTasksModal }
        onClose={ () => { setOpenTasksModal(false); setTaskModalTasks(null); setModalDateFilter("All"); setModalCustomStart(""); setModalCustomEnd(""); } }
        maxWidth="lg"
        PaperProps={ { style: { width: 980, maxHeight: '85vh' } } }
      >
        <DialogTitle style={ { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 } }>
          <span style={{ fontWeight: 700, fontSize: "1.5rem" }}>Tasks Pipeline</span>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            {/* Date Range filter */}
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <label style={{ fontSize: "0.68rem", color: "#6b7280", fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", marginLeft: 1 }}>Date Range</label>
              <select
                value={modalDateFilter}
                onChange={e => { setModalDateFilter(e.target.value); if (e.target.value !== "custom") { setModalCustomStart(""); setModalCustomEnd(""); } }}
                style={{
                  padding: "7px 32px 7px 11px", borderRadius: 8,
                  border: "1.5px solid #d1d5db", fontSize: "0.85rem",
                  background: "#fff", cursor: "pointer", minWidth: 140,
                  color: "#111827", fontWeight: 500,
                  appearance: "none", WebkitAppearance: "none",
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 10px center",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                }}
              >
                <option value="All">All</option>
                <option value="7">Last 7 Days</option>
                <option value="30">Last 30 Days</option>
                <option value="90">Last 90 Days</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>
            {modalDateFilter === "custom" && (
              <>
                <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <label style={{ fontSize: "0.68rem", color: "#6b7280", fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", marginLeft: 1 }}>Start</label>
                  <input type="date" value={modalCustomStart} onChange={e => setModalCustomStart(e.target.value)}
                    style={{ padding: "7px 9px", borderRadius: 8, border: "1.5px solid #d1d5db", fontSize: "0.85rem", color: "#111827", fontWeight: 500, boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }} />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <label style={{ fontSize: "0.68rem", color: "#6b7280", fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", marginLeft: 1 }}>End</label>
                  <input type="date" value={modalCustomEnd} onChange={e => setModalCustomEnd(e.target.value)}
                    style={{ padding: "7px 9px", borderRadius: 8, border: "1.5px solid #d1d5db", fontSize: "0.85rem", color: "#111827", fontWeight: 500, boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }} />
                </div>
              </>
            )}
            {modalDateFilter !== "All" && (
              <button
                onClick={() => { setModalDateFilter("All"); setModalCustomStart(""); setModalCustomEnd(""); }}
                style={{
                  padding: "7px 16px", borderRadius: 8,
                  border: "1.5px solid #d1d5db", background: "#fff",
                  cursor: "pointer", fontWeight: 600, color: "#374151",
                  fontSize: "0.85rem", alignSelf: "flex-end",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                  transition: "all 0.15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "#9ca3af"; e.currentTarget.style.background = "#f9fafb"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "#d1d5db"; e.currentTarget.style.background = "#fff"; }}
              >
                Clear Filters
              </button>
            )}
            <IconButton size="small" onClick={ () => { setOpenTasksModal(false); setTaskModalTasks(null); setModalDateFilter("All"); setModalCustomStart(""); setModalCustomEnd(""); } } style={{ alignSelf: "flex-end" }}>
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent dividers style={ { padding: 16, overflowY: 'auto' } }>
          { (() => {
            const tasks = taskModalTasks || [];
            const STATUSES = ["Pending", "Overdue", "Completed"];
            const STATUS_CFG = {
              Pending:   { badge: "#f97316", light: "#fff7ed", border: "#fde8c8", leftBar: "#f97316" },
              Overdue:   { badge: "#dc2626", light: "#fef2f2", border: "#fecaca", leftBar: "#dc2626" },
              Completed: { badge: "#16a34a", light: "#f0fdf4", border: "#c3f0d0", leftBar: "#16a34a" },
            };
            return (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
                { STATUSES.map(status => {
                  const cfg = STATUS_CFG[status];
                  const colTasks = tasks.filter(t => (t.status || "Pending") === status);
                  return (
                    <div key={status} style={{ background: cfg.light, border: `1px solid ${cfg.border}`, borderRadius: 12, padding: "14px 12px", minHeight: 200 }}>
                      {/* Column header */}
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                        <span style={{ fontWeight: 700, fontSize: "0.95rem", color: "#111827" }}>{status}</span>
                        <span style={{ background: "#fff", border: `1px solid ${cfg.border}`, borderRadius: 20, padding: "1px 10px", fontSize: "0.78rem", fontWeight: 700, color: "#64748b" }}>
                          {colTasks.length}
                        </span>
                      </div>
                      {/* Task cards */}
                      { colTasks.length === 0 ? (
                        <div style={{ textAlign: "center", color: "#9ca3af", fontSize: "0.82rem", paddingTop: 24 }}>
                          No {status.toLowerCase()} tasks
                        </div>
                      ) : (
                        colTasks.map(task => {
                          const assigneeName = task.assignedUser?.name || task.assignedUser?.email ||
                            (task.assignedTo && typeof task.assignedTo === "object" ? task.assignedTo.name || task.assignedTo.email : null);
                          const dueDate = task.dueDate ? new Date(task.dueDate).toLocaleDateString("en-CA") : null;
                          return (
                            <div key={task._id || task.id} style={{
                              background: "#fff", borderRadius: 8, padding: "12px 12px 12px 0",
                              marginBottom: 10, boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                              borderLeft: `4px solid ${cfg.leftBar}`, paddingLeft: 12,
                              display: "flex", flexDirection: "column", gap: 6,
                            }}>
                              <div style={{ fontWeight: 600, fontSize: "0.9rem", color: "#111827", lineHeight: 1.4 }}>
                                {task.taskTitle || task.title || task.name || "Untitled"}
                              </div>
                              <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                                { assigneeName && (
                                  <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.78rem", color: "#6b7280" }}>
                                    👤 {assigneeName}
                                  </span>
                                )}
                                { dueDate && (
                                  <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.78rem", color: "#6b7280" }}>
                                    📅 {dueDate}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })() }
        </DialogContent>
      </Dialog>

  
     

      {/* Fullscreen Modal for Graphs */ }
      <Dialog
        open={ fullscreenModal }
        onClose={ () => { setFullscreenModal(false); setSelectedGraphType(null); } }
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          style={ {
            display: "flex",
            alignItems: "center",
            paddingRight: 16
          } }
        >
          <span style={ { fontSize: 18, fontWeight: 600 } }>{ selectedGraph?.title || "Graph" }</span>
          <div style={ { marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 } }>
            <IconButton
              size="small"
              sx={ { padding: "4px" } }
              onClick={ (e) => {
                if (selectedGraphType === 'funnel') setFunnelAnchor(e.currentTarget);
                else if (selectedGraphType === 'sources') setSourcesAnchor(e.currentTarget);
                else if (selectedGraphType === 'performance') setPerfAnchor(e.currentTarget);
                else if (selectedGraphType === 'tasks') setTaskStatsAnchor(e.currentTarget);
              } }
              aria-label="date-filter"
            >
              <CalendarTodayIcon fontSize="small" />
            </IconButton>
            
            <button
              onClick={ () => { setFullscreenModal(false); setSelectedGraphType(null); } }
              style={ {
                fontWeight: 600,
                background: "none",
                border: "1px solid #ccc",
                padding: "4px 12px",
                borderRadius: 4,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontSize: 13,
                height: 30
              } }
            >
              Back
            </button>
            <IconButton
              size="small"
              onClick={ () => {
                let dataToDownload = null;
                if (selectedGraphType === 'funnel') {
                  const stageDetails = (pipelineSummary && pipelineSummary.stageDetails) || [];
                  dataToDownload = { labels: stageDetails.map(s => s.stageName), datasets: [{ label: "Overall Lead Funnel", data: stageDetails.map(s => s.total || 0) }] };
                } else if (selectedGraphType === 'sources') {
                  const stageDetails = (leadSourceMetrics && leadSourceMetrics.leadSourceMetrics) || [];
                  dataToDownload = { labels: stageDetails.map(s => s.source_name), datasets: [{ label: "Lead Count", data: stageDetails.map(s => s.lead_count || 0) }] };
                } else if (selectedGraphType === 'performance') {
                  const userData = performanceMetrics?.byMember?.find(u => String(u.userId) === String(id));
                  if (userData) {
                    const winsValue = (userData.teamType === 'telesales' ? userData.telesales_wins : userData.sales_wins) || 0;
                    dataToDownload = { labels: ['Wins', 'Closed Lost', 'Open'], datasets: [{ label: 'Performance', data: [winsValue, userData.breakdown?.closed_lost || 0, (userData.open_items || []).length] }] };
                  }
                } else if (selectedGraphType === 'tasks') {
                  const dataArray = (teamTaskStats?.data) || (teamTaskStats?.byMember) || [];
                  const userData = dataArray.find(u => String(u.memberId) === String(id) || String(u.userId) === String(id));
                  if (userData) {
                    const counts = userData.statusCounts || {};
                    dataToDownload = { labels: ['Pending', 'Overdue', 'Completed'], datasets: [{ label: 'Tasks', data: [counts.Pending || 0, counts.Overdue || 0, counts.Completed || 0] }] };
                  }
                } else if (selectedGraphType === 'questions' && questionsData && questionsData.length > 0) {
                  dataToDownload = { labels: questionsData.map(q => q.question), datasets: [{ label: 'Frequency', data: questionsData.map(q => q.frequency || 1) }] };
                }
                if (dataToDownload) {
                  const fname = `${(member?.name || 'user').toString().replace(/\s+/g, '_').toLowerCase()}_${(selectedGraph?.title || 'chart').replace(/\s+/g, '_').toLowerCase()}.csv`;
                  downloadChartCSV(dataToDownload, { filename: fname, includeAllDatasets: true });
                }
              } }
              title="Download CSV"
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={ () => { setFullscreenModal(false); setSelectedGraphType(null); } } title="Close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent dividers style={ { height: 600, padding: 24 } }>
          <div style={ { height: '100%', width: '100%' } }>
            { selectedGraphType === 'funnel' && renderPipelineSummary() }
            { selectedGraphType === 'sources' && renderLeadSourceMetrics() }
            { selectedGraphType === 'performance' && renderPerformanceMetrics() }
            { selectedGraphType === 'tasks' && renderTaskStats() }
            { selectedGraphType === 'questions' && renderTopQuestions(true) }
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
